import { useEffect, useMemo, useState } from "react";
import LegalOnboardingFrame from "./LegalOnboardingFrame";
import {
  AGENT_PLAN,
  APP_HOME_URL,
  MEMBERSHIP_PLANS,
  SEAT_TIERS,
  writeMembershipActivation,
} from "../config";
import "./membership-enrollment-modal.css";

const EMPTY_PAY = {
  name: "",
  email: "",
  card: "",
  expiry: "",
  cvc: "",
};

function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "Custom";
  return n % 1 === 0 ? `$${n}` : `$${n.toFixed(2)}`;
}

export default function MembershipEnrollmentModal({
  isOpen,
  onClose,
  initialPlanId = "agent",
  initialBrokerTier = "10",
  cycle = "monthly",
}) {
  const [step, setStep] = useState(1);
  const [planId, setPlanId] = useState(initialPlanId || "agent");
  const [brokerTier, setBrokerTier] = useState(initialBrokerTier || "10");
  const [pay, setPay] = useState(EMPTY_PAY);
  const [payError, setPayError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setStep(1);
    setPlanId(initialPlanId || "agent");
    setBrokerTier(initialBrokerTier || "10");
    setPay(EMPTY_PAY);
    setPayError("");
    setBusy(false);
    writeMembershipActivation({
      status: "locked",
      reason: "enrollment_in_progress",
      planId: initialPlanId || "agent",
      cycle,
    });
  }, [isOpen, initialPlanId, initialBrokerTier, cycle]);

  const plan = useMemo(
    () => MEMBERSHIP_PLANS.find((row) => row.id === planId) || AGENT_PLAN,
    [planId]
  );
  const seat = useMemo(
    () => SEAT_TIERS.find((row) => row.id === brokerTier) || SEAT_TIERS[0],
    [brokerTier]
  );

  const priceLabel = useMemo(() => {
    if (plan.custom) return "Custom Pricing";
    if (plan.id === "broker") {
      return cycle === "annual"
        ? `${formatMoney(seat.annual)} / year`
        : `${formatMoney(seat.monthly)} / month`;
    }
    return cycle === "annual"
      ? `${formatMoney(plan.annual)} / year`
      : `${formatMoney(plan.monthly)} / month`;
  }, [plan, seat, cycle]);

  if (!isOpen) return null;

  function updatePay(event) {
    const { name, value } = event.target;
    setPay((current) => ({ ...current, [name]: value }));
  }

  function continueFromPlan() {
    if (plan.custom) {
      onClose?.();
      window.location.assign("/contact");
      return;
    }
    setStep(2);
  }

  function confirmPayment(event) {
    event.preventDefault();
    setPayError("");
    if (
      !pay.name.trim() ||
      !pay.email.trim() ||
      !pay.card.trim() ||
      !pay.expiry.trim() ||
      !pay.cvc.trim()
    ) {
      setPayError("Please complete all payment fields to continue.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(pay.email.trim())) {
      setPayError("Enter a valid billing email.");
      return;
    }
    setBusy(true);
    writeMembershipActivation({
      status: "locked",
      reason: "awaiting_agreement",
      planId: plan.id,
      tier: plan.id === "broker" ? brokerTier : "",
      cycle,
      paymentConfirmed: true,
      payerEmail: pay.email.trim(),
    });
    setBusy(false);
    setStep(3);
  }

  function acceptLegal() {
    writeMembershipActivation({
      status: "active",
      reason: "activated",
      planId: plan.id,
      tier: plan.id === "broker" ? brokerTier : "",
      cycle,
      paymentConfirmed: true,
      agreementAccepted: true,
      activatedAt: new Date().toISOString(),
      payerEmail: pay.email.trim(),
    });
    setStep(4);
  }

  function enterSystem() {
    const role = plan.id === "broker" ? "broker" : "agent";
    const path = role === "broker" ? "/broker/dashboard" : "/agent/dashboard";
    const url = new URL(path, APP_HOME_URL);
    url.searchParams.set("membership", "activated");
    url.searchParams.set("plan", plan.id);
    if (plan.id === "broker") url.searchParams.set("tier", brokerTier);
    url.searchParams.set("cycle", cycle);
    window.location.assign(url.toString());
  }

  return (
    <div className="mem-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="mem-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mem-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="mem-modal__header">
          <div>
            <p className="mem-modal__kicker">Membership enrollment</p>
            <h2 id="mem-modal-title">Join DreamHome Journey</h2>
          </div>
          <button type="button" className="mem-modal__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        <ol className="mem-modal__steps" aria-label="Enrollment steps">
          <li className={step === 1 ? "is-active" : step > 1 ? "is-done" : ""}>Plan</li>
          <li className={step === 2 ? "is-active" : step > 2 ? "is-done" : ""}>Payment</li>
          <li className={step === 3 ? "is-active" : step > 3 ? "is-done" : ""}>Agreement</li>
          <li className={step === 4 ? "is-active" : ""}>Activation</li>
        </ol>

        <div className="mem-modal__body">
          {step === 1 ? (
            <div className="mem-step">
              <h3>Select your membership</h3>
              <p className="mem-step__lead">
                Choose the plan that matches your workflow. Billing cycle follows the page toggle (
                {cycle === "annual" ? "Annual" : "Monthly"}).
              </p>
              <div className="mem-plan-grid" role="radiogroup" aria-label="Membership plan">
                {MEMBERSHIP_PLANS.map((row) => (
                  <label
                    key={row.id}
                    className={`mem-plan-option${planId === row.id ? " is-selected" : ""}`}
                  >
                    <input
                      type="radio"
                      name="membership-plan"
                      value={row.id}
                      checked={planId === row.id}
                      onChange={() => setPlanId(row.id)}
                    />
                    <span className="mem-plan-option__name">{row.name}</span>
                    <span className="mem-plan-option__sub">{row.subtitle}</span>
                  </label>
                ))}
              </div>
              {planId === "broker" ? (
                <label className="mem-field">
                  <span>Office size</span>
                  <select value={brokerTier} onChange={(event) => setBrokerTier(event.target.value)}>
                    {SEAT_TIERS.map((row) => (
                      <option key={row.id} value={row.id}>
                        {row.name} —{" "}
                        {cycle === "annual"
                          ? `${formatMoney(row.annual)}/yr`
                          : `${formatMoney(row.monthly)}/mo`}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              <p className="mem-price-summary">
                <strong>{plan.name}</strong>
                <span>{priceLabel}</span>
              </p>
              <div className="mem-actions">
                <button type="button" className="mem-btn" onClick={continueFromPlan}>
                  {plan.custom ? "Contact Us" : "Continue to Payment"}
                </button>
              </div>
            </div>
          ) : null}

          {step === 2 ? (
            <form className="mem-step" onSubmit={confirmPayment}>
              <h3>Secure payment</h3>
              <p className="mem-step__lead">
                Enter billing details for <strong>{plan.name}</strong> ({priceLabel}). Payment stays
                in this enrollment window — no external checkout redirect.
              </p>
              <div className="mem-pay-grid">
                <label className="mem-field">
                  <span>Cardholder name</span>
                  <input name="name" value={pay.name} onChange={updatePay} autoComplete="cc-name" />
                </label>
                <label className="mem-field">
                  <span>Billing email</span>
                  <input
                    name="email"
                    type="email"
                    value={pay.email}
                    onChange={updatePay}
                    autoComplete="email"
                  />
                </label>
                <label className="mem-field mem-field--full">
                  <span>Card number</span>
                  <input
                    name="card"
                    value={pay.card}
                    onChange={updatePay}
                    inputMode="numeric"
                    autoComplete="cc-number"
                    placeholder="•••• •••• •••• ••••"
                  />
                </label>
                <label className="mem-field">
                  <span>Expiry</span>
                  <input
                    name="expiry"
                    value={pay.expiry}
                    onChange={updatePay}
                    autoComplete="cc-exp"
                    placeholder="MM/YY"
                  />
                </label>
                <label className="mem-field">
                  <span>CVC</span>
                  <input
                    name="cvc"
                    value={pay.cvc}
                    onChange={updatePay}
                    autoComplete="cc-csc"
                    placeholder="•••"
                  />
                </label>
              </div>
              {payError ? <p className="mem-error">{payError}</p> : null}
              <div className="mem-actions">
                <button type="button" className="mem-btn mem-btn--ghost" onClick={() => setStep(1)}>
                  Back
                </button>
                <button type="submit" className="mem-btn" disabled={busy}>
                  {busy ? "Confirming…" : "Confirm Payment"}
                </button>
              </div>
            </form>
          ) : null}

          {step === 3 ? (
            <div className="mem-step mem-step--legal">
              <LegalOnboardingFrame onAccept={acceptLegal} />
            </div>
          ) : null}

          {step === 4 ? (
            <div className="mem-step">
              <h3>Membership activated</h3>
              <p className="mem-step__lead">
                Payment is confirmed and your agreements are accepted. Access to REChecklist is
                unlocked for this membership.
              </p>
              <p className="mem-price-summary">
                <strong>{plan.name}</strong>
                <span>{priceLabel}</span>
              </p>
              <div className="mem-actions">
                <button type="button" className="mem-btn" onClick={enterSystem}>
                  Enter REChecklist
                </button>
              </div>
            </div>
          ) : null}
        </div>

        <p className="mem-modal__footnote">
          Access remains locked until payment and agreement are complete — including if you open
          another tab.
        </p>
      </div>
    </div>
  );
}
